import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/base64-file-size-overhead';

const FAQS: Faq[] = [
  { q: 'Why does Base64 make files about 33% larger?', a: 'Base64 represents every 3 bytes (24 bits) of input as 4 output characters (6 bits each). 4 output characters for 3 input bytes is a 4/3 ratio — a 33.3% increase.' },
  { q: 'What is the exact formula for encoded size?', a: 'ceil(inputBytes / 3) * 4. This accounts for padding: inputs whose length is not a multiple of 3 get one or two "=" padding characters so the output length is always a multiple of 4.' },
  { q: 'Does the overhead change for URL-safe or MIME Base64?', a: 'URL-safe Base64 without padding is marginally smaller (it drops the trailing "=" characters). MIME Base64 is marginally larger, since it adds a line break every 76 characters.' },
  { q: 'How can I calculate this for my own file?', a: 'Use the Base64 Size Calculator to enter a byte count or paste text and see the exact encoded size and overhead percentage.' },
];

export default function Base64FileSizeOverheadPage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="Why Base64 Makes Files About 33% Larger | EasyBase64"
        description="A clear walkthrough of the math behind Base64's ~33% size overhead, the exact formula, and when the overhead actually matters."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'Why Base64 Makes Files About 33% Larger',
            description: 'The math behind Base64\'s size overhead and when it matters in practice.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Base64 File Size Overhead', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            Why Base64 Makes Files About 33% Larger
          </h1>

          <p className={para}>
            If you've ever compared a file's size before and after Base64 encoding, you've noticed
            the encoded version is noticeably bigger — consistently about a third larger. That
            overhead isn't a quirk of any particular tool; it's a direct consequence of how Base64
            packs bits.
          </p>

          <h2 className={heading}>The bit math</h2>
          <p className={para}>
            Base64 groups input into chunks of 3 bytes (24 bits) and splits each chunk into four
            6-bit groups, mapping each group to one of 64 printable characters. Three input bytes
            (24 bits) always produce exactly four output characters (24 bits' worth of 6-bit
            groups) — so the size ratio is fixed at 4/3, or a 33.3% increase, regardless of what the
            data actually contains.
          </p>

          <h2 className={heading}>The exact formula</h2>
          <p className={para}>
            Encoded length in characters = <code>ceil(inputBytes / 3) * 4</code>. The{' '}
            <code>ceil</code> (round up) accounts for inputs that aren't an exact multiple of 3
            bytes: the final group gets padded with one or two <code>=</code> characters so the
            total length is always a multiple of 4. For example, a 10-byte input:{' '}
            <code>ceil(10/3)*4 = 4*4 = 16</code> characters.
          </p>

          <h2 className={heading}>Some worked examples</h2>
          <p className={para}>
            1&nbsp;KB (1,024 bytes) of data encodes to <code>ceil(1024/3)*4 = 1,368</code>{' '}
            characters — 1,368 bytes as ASCII text, a 33.6% increase. A 1&nbsp;MB image encodes to
            roughly 1.37&nbsp;MB of Base64 text. Try your own numbers with the{' '}
            <Link to="/base64-size-calculator" className="underline">Base64 Size Calculator</Link>.
          </p>

          <h2 className={heading}>When the overhead actually matters</h2>
          <p className={para}>
            For small text snippets, tokens, or config values, a 33% increase is negligible. It
            starts to matter for larger binary payloads: embedding a large image as a{' '}
            <Link to="/base64-data-uri-guide" className="underline">data URI</Link>, attaching big
            files to{' '}
            <Link to="/base64-for-email-attachments" className="underline">email via MIME</Link>, or{' '}
            <Link to="/base64-database-storage" className="underline">storing large blobs as Base64 text in a database</Link>{' '}
            column — in each case, weigh the convenience of text-safe encoding against the extra
            bandwidth, storage, and (for HTTP responses) potential compression inefficiency the
            overhead introduces.
          </p>
        </article>

        <FaqSection title="Base64 File Size Overhead — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
